import { useTranslation } from "react-i18next";
import { CheckCircle2, Library } from "lucide-react";

import { brandAssetPath } from "@shared/config";
import { useSiteFeatureFlags } from "@shared/hooks/use-site-feature-flags";
import { isFeatureOn } from "@shared/lib/site-features";
import { cn } from "@shared/lib/utils";

const KNOWLEDGE_CARDS = ["protocols", "knowledge", "community", "library"] as const;

/** Card → site feature it advertises; disabled features are not marketed. */
const CARD_FEATURES: Record<(typeof KNOWLEDGE_CARDS)[number], string> = {
  protocols: "diet.protocols",
  knowledge: "knowledge",
  community: "community",
  library: "knowledge.books",
};

const VISUAL_IMAGES = [
  "assets/diet-protocols/autoimmune-protocol.webp",
  "assets/diet-protocols/carnivore.webp",
  "assets/diet-protocols/ketogenic.webp",
] as const;

export function KnowledgeSection() {
  const { t } = useTranslation("landing");
  const assetBase = import.meta.env.BASE_URL;
  const { data: flags } = useSiteFeatureFlags();
  const cards = KNOWLEDGE_CARDS.filter((card) => isFeatureOn(flags, CARD_FEATURES[card]));
  if (cards.length === 0) return null;

  return (
    <section
      id="knowledge"
      className="bg-background py-20 sm:py-24 lg:py-28"
      aria-labelledby="knowledge-heading"
    >
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1fr] lg:px-8">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-primary">
            {t("knowledge.eyebrow")}
          </p>
          <h2
            id="knowledge-heading"
            className="mt-4 font-serif text-4xl font-semibold leading-[1.1] tracking-[-0.04em] text-foreground sm:text-5xl lg:text-6xl"
          >
            {t("knowledge.heading")}
          </h2>
          <p className="mt-5 text-lg leading-8 text-muted-foreground">
            {t("knowledge.subheading")}
          </p>
          <div className="mt-8 space-y-4">
            {cards.map((card) => {
              const isLibrary = card === "library";
              return (
                <div
                  key={card}
                  className={cn(
                    "group flex gap-4 rounded-2xl border bg-card p-5 transition-colors",
                    isLibrary
                      ? "border-primary/30 bg-primary/5 hover:border-primary/50"
                      : "border-border hover:border-primary/30",
                  )}
                >
                  <span
                    className={cn(
                      "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
                      isLibrary
                        ? "bg-primary text-primary-foreground"
                        : "bg-primary/10 text-primary",
                    )}
                  >
                    {isLibrary ? (
                      <Library className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
                    )}
                  </span>
                  <div>
                    <h3 className="font-semibold text-foreground">
                      {t(`knowledge.cards.${card}.title`)}
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                      {t(`knowledge.cards.${card}.description`)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative">
          <div
            className="absolute end-4 top-8 h-40 w-40 rounded-full bg-primary/10 blur-3xl"
            aria-hidden="true"
          />
          <div className="grid grid-cols-2 gap-4">
            {VISUAL_IMAGES.map((src, index) => (
              <img
                key={src}
                src={brandAssetPath(assetBase, src)}
                alt={t(`knowledge.imageAlt${index + 1}`)}
                width={320}
                height={320}
                loading="lazy"
                className={cn(
                  "aspect-square rounded-3xl border border-border bg-muted object-cover shadow-lg",
                  index === 1 && "mt-10",
                  index === 2 && "col-span-2 mx-auto max-w-[70%]",
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default KnowledgeSection;
